import React, { useMemo } from 'react';
import { axisTooltipFormatter } from '@common/utils/chartTooltipFormatter';
import { truncateLabel } from '@common/utils/chartUtils';
import ChartWidgetContainer from '@common/components/ChartWidgetContainer';

// ECharts color palette - ECharts doesn't support CSS variables
const CHART_COLORS = [
  '#5470c6', // blue
  '#91cc75', // green
  '#fac858', // yellow
  '#ee6666', // red
  '#73c0de', // cyan
  '#3ba272', // teal
  '#fc8452', // orange
  '#9a60b4', // purple
  '#ea7ccc', // pink
  '#48b8d0', // light blue
];

function LineChartWidget({ widget }) {
  const { data } = widget;

  const option = useMemo(() => {
    if (!data) return null;

    // Support both single series (data.values) and multi-series (data.series)
    const isMultiSeries = data.series && Array.isArray(data.series);
    
    // Check if dual Y-axis is enabled
    const useDualYAxis = data.dualYAxis === true;

    // Detect if Y-axis values are categorical (strings) vs numeric
    const detectCategoricalYAxis = () => {
      let allValues = [];
      if (isMultiSeries) {
        (data.series || []).forEach((s) => {
          if (s.data) allValues = allValues.concat(s.data);
        });
      } else if (data.values) {
        allValues = data.values;
      }
      // Filter out null/undefined so they don't become a "null" category
      const validValues = allValues.filter((v) => v !== null && v !== undefined);
      const hasStringValues = validValues.some((v) => typeof v === 'string' && Number.isNaN(Number(v)));
      if (hasStringValues) {
        const seen = new Set();
        const categories = [];
        validValues.forEach((v) => {
          const strVal = String(v);
          if (!seen.has(strVal)) {
            seen.add(strVal);
            categories.push(strVal);
          }
        });
        return { isCategorical: true, categories };
      }
      return { isCategorical: false, categories: [] };
    };

    const { isCategorical: isYAxisCategorical, categories: yAxisCategories } = detectCategoricalYAxis();
    
    // Detect if a specific Y-axis (by index) has categorical/string values
    const detectAxisType = (yAxisIndex) => {
      if (!isMultiSeries) return { type: 'value', categories: [] };
      
      // Get all values for series with this yAxisIndex (with null safety)
      const seriesForAxis = (data.series || []).filter(s => s.yAxisIndex === yAxisIndex);
      const allValues = seriesForAxis.flatMap(s => s.data || []).filter(v => v !== null);
      
      // Check if values are strings (non-numeric)
      const hasStringValues = allValues.some(v => typeof v === 'string' && Number.isNaN(Number(v)));
      if (hasStringValues) {
        // Get unique categories
        const seen = new Set();
        const categories = [];
        allValues.forEach(v => {
          const strVal = String(v);
          if (!seen.has(strVal)) {
            seen.add(strVal);
            categories.push(strVal);
          }
        });
        return { type: 'category', categories };
      }
      return { type: 'value', categories: [] };
    };

    // Build Y-axis configuration
    const buildYAxis = () => {
      // Dual Y-axis takes priority (when combining different data sources)
      if (useDualYAxis) {
        // Dual Y-axes: left (index 0) and right (index 1)
        const leftAxisConfig = data.yAxisConfig?.left || {};
        const rightAxisConfig = data.yAxisConfig?.right || {};
        
        // Detect axis types based on data
        const leftAxisType = detectAxisType(0);
        const rightAxisType = detectAxisType(1);
        
        return [
          {
            ...leftAxisConfig,
            type: leftAxisType.type,
            data: leftAxisType.type === 'category' ? leftAxisType.categories : undefined,
            name: truncateLabel(leftAxisConfig.name),
            position: 'left',
            axisLabel: {
              ...leftAxisConfig.axisLabel,
              fontSize: 11,
              formatter: (value) => truncateLabel(value),
            },
            nameTextStyle: {
              ...leftAxisConfig.nameTextStyle,
              fontSize: 11,
            },
            tooltip: {
              show: true,
              formatter: (params) => params.value,
            },
          },
          {
            ...rightAxisConfig,
            type: rightAxisType.type,
            data: rightAxisType.type === 'category' ? rightAxisType.categories : undefined,
            name: truncateLabel(rightAxisConfig.name),
            position: 'right',
            axisLabel: {
              ...rightAxisConfig.axisLabel,
              fontSize: 11,
              formatter: (value) => truncateLabel(value),
            },
            nameTextStyle: {
              ...rightAxisConfig.nameTextStyle,
              fontSize: 11,
            },
            tooltip: {
              show: true,
              formatter: (params) => params.value,
            },
          },
        ];
      }

      // Categorical Y-axis (for string values like "OK", "UP") - only when NOT dual Y-axis
      if (isYAxisCategorical) {
        const yAxisConfig = data.yAxisConfig || {};
        return {
          ...yAxisConfig,
          type: 'category',
          data: yAxisCategories,
          axisLabel: {
            ...yAxisConfig.axisLabel,
            fontSize: 11,
            formatter: (value) => truncateLabel(value),
          },
          tooltip: {
            show: true,
            formatter: (params) => params.value,
          },
        };
      }
      
      // Single Y-axis
      const yAxisConfig = data.yAxisConfig || {};
      return {
        ...yAxisConfig,
        type: 'value',
        axisLabel: {
          ...yAxisConfig.axisLabel,
          fontSize: 11,
        },
      };
    };
    
    // Helper to get yAxisIndex for series
    const getSeriesYAxisIndex = (seriesYAxisIndex) => {
      if (!useDualYAxis) return undefined;
      if (seriesYAxisIndex !== undefined) return seriesYAxisIndex;
      return 0;
    };

    // Build series configuration
    const buildSeries = () => {
      if (isMultiSeries) {
        return (data.series || []).map((series, index) => {
          const color = series.color || CHART_COLORS[index % CHART_COLORS.length];
          return {
            name: series.name,
            type: 'line',
            data: series.data,
            yAxisIndex: getSeriesYAxisIndex(series.yAxisIndex),
            itemStyle: { color },
            lineStyle: { color },
            areaStyle: series.showArea ? {
              color: `${color}33`, // 20% opacity
            } : undefined,
            smooth: true,
          };
        });
      }
      
      const color = data.color || CHART_COLORS[0];
      return [
        {
          name: widget.title,
          type: 'line',
          data: data.values || [],
          yAxisIndex: getSeriesYAxisIndex(data.yAxisIndex),
          itemStyle: { color },
          lineStyle: { color },
          areaStyle: data.showArea ? {
            color: `${color}33`, // 20% opacity
          } : undefined,
          smooth: true,
        },
      ];
    };

    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        appendToBody: true,
        formatter: axisTooltipFormatter,
      },
      legend: isMultiSeries ? {
        data: (data.series || []).map(s => s.name),
        textStyle: {
          fontSize: 11,
        },
        formatter: (name) => truncateLabel(name),
        tooltip: {
          show: true,
          formatter: (params) => params.name,
        },
        type: 'scroll',
        bottom: 0,
      } : undefined,
      grid: {
        left: useDualYAxis ? '10%' : '3%',
        right: useDualYAxis ? '10%' : '4%',
        top: '10%',
        bottom: isMultiSeries ? '20%' : '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: data.categories || [],
        axisLabel: {
          fontSize: 11,
          rotate: 45,
          formatter: (value) => truncateLabel(value),
        },
        tooltip: {
          show: true,
          formatter: (params) => params.value,
        },
      },
      yAxis: buildYAxis(),
      series: buildSeries(),
    };
  }, [data, widget.title]);

  return <ChartWidgetContainer widget={widget} option={option} style={{ minHeight: '300px' }} />;
}

export default LineChartWidget;
